/**
 * Manual Content Component
 *
 * Renders markdown content with custom components and styling.
 */

import React, { useEffect, useState, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import { ManualImage } from './ManualImage';
import { extractHeadings, generateAnchorId, normalizeImagePath, throttle } from '../../utils/markdown';
import type { TableOfContentItem } from '../../types/manual';
import './ManualContent.css';
import 'highlight.js/styles/github-dark.css';

interface ManualContentProps {
  /** Markdown content to render */
  content: string;

  /** Callback when table of contents is extracted */
  onTableOfContentsExtracted?: (toc: TableOfContentItem[]) => void;

  /** Base path for resolving relative image paths */
  basePath?: string;
}

export const ManualContent = ({
  content,
  onTableOfContentsExtracted,
  basePath = '/docs/delivery',
}: ManualContentProps) => {
  const [activeHeadingId, setActiveHeadingId] = useState<string>('');

  // Extract table of contents on mount
  useEffect(() => {
    const toc = extractHeadings(content);
    onTableOfContentsExtracted?.(toc);
  }, [content, onTableOfContentsExtracted]);

  // Track active heading on scroll
  useEffect(() => {
    const handleScroll = throttle(() => {
      const headings = document.querySelectorAll('[id^="heading-"]');
      const scrollPosition = window.scrollY + 150; // Offset for header

      let currentHeading = '';

      headings.forEach((heading) => {
        if (heading instanceof HTMLElement) {
          const headingTop = heading.offsetTop;

          if (headingTop <= scrollPosition) {
            currentHeading = heading.id;
          }
        }
      });

      setActiveHeadingId(currentHeading);
    }, 100);

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Initial check

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Custom components for markdown rendering
  // Memoize to prevent unnecessary re-renders of images when scrolling
  const components = useMemo(() => ({
    // Headings with anchor IDs
    h1: ({ children, ...props }: any) => {
      const text = typeof children === 'string' ? children : children?.[0] || '';
      const id = generateAnchorId(String(text));
      return (
        <h1 id={id} className={activeHeadingId === id ? 'active-heading' : ''} {...props}>
          {children}
        </h1>
      );
    },
    h2: ({ children, ...props }: any) => {
      const text = typeof children === 'string' ? children : children?.[0] || '';
      const id = generateAnchorId(String(text));
      return (
        <h2 id={id} className={activeHeadingId === id ? 'active-heading' : ''} {...props}>
          {children}
        </h2>
      );
    },
    h3: ({ children, ...props }: any) => {
      const text = typeof children === 'string' ? children : children?.[0] || '';
      const id = generateAnchorId(String(text));
      return (
        <h3 id={id} className={activeHeadingId === id ? 'active-heading' : ''} {...props}>
          {children}
        </h3>
      );
    },
    h4: ({ children, ...props }: any) => {
      const text = typeof children === 'string' ? children : children?.[0] || '';
      const id = generateAnchorId(String(text));
      return (
        <h4 id={id} className={activeHeadingId === id ? 'active-heading' : ''} {...props}>
          {children}
        </h4>
      );
    },
    h5: ({ children, ...props }: any) => {
      const text = typeof children === 'string' ? children : children?.[0] || '';
      const id = generateAnchorId(String(text));
      return (
        <h5 id={id} className={activeHeadingId === id ? 'active-heading' : ''} {...props}>
          {children}
        </h5>
      );
    },
    h6: ({ children, ...props }: any) => {
      const text = typeof children === 'string' ? children : children?.[0] || '';
      const id = generateAnchorId(String(text));
      return (
        <h6 id={id} className={activeHeadingId === id ? 'active-heading' : ''} {...props}>
          {children}
        </h6>
      );
    },

    // Paragraphs
    p: ({ children, node, ...props }: any) => {
      // Check if paragraph contains an image to avoid HTML nesting errors
      // <p> cannot contain block-level elements like <figure> or <div>

      // First check: look at the raw markdown node (most reliable)
      const hasImageInNode = node?.children?.some((child: any) =>
        child?.tagName === 'img'
      );

      // Second check: look at the rendered React children
      const hasImageInChildren = React.Children.toArray(children).some((child: any) => {
        if (React.isValidElement(child)) {
          // Check by component type
          if (child.type === ManualImage) return true;
          // Check by props (react-markdown passes original node in props.node)
          if ((child.props as any)?.node?.tagName === 'img') return true;
          // Check by element type name
          if (typeof child.type === 'function' && (child.type as any).name === 'ManualImage') return true;
        }
        return false;
      });

      const hasImage = hasImageInNode || hasImageInChildren;

      // Use div for paragraphs with images to avoid nesting errors
      const Tag = hasImage ? 'div' : 'p';
      return <Tag {...props}>{children}</Tag>;
    },

    // Links
    a: ({ href, children, ...props }: any) => (
      <a
        href={href}
        target={href?.startsWith('http') ? '_blank' : undefined}
        rel={href?.startsWith('http') ? 'noopener noreferrer' : undefined}
        {...props}
      >
        {children}
      </a>
    ),

    // Images with fallback
    img: ({ src, alt, ...props }: any) => {
      const normalizedSrc = normalizeImagePath(src, basePath);
      return <ManualImage src={normalizedSrc} alt={alt || ''} {...props} />;
    },

    // Lists
    ul: ({ children, ...props }: any) => <ul {...props}>{children}</ul>,
    ol: ({ children, ...props }: any) => <ol {...props}>{children}</ol>,
    li: ({ children, ...props }: any) => <li {...props}>{children}</li>,

    // Code blocks
    code: ({ inline, className, children, ...props }: any) => {
      if (inline) {
        return <code className="inline-code" {...props}>{children}</code>;
      }
      return <code className={className} {...props}>{children}</code>;
    },
    pre: ({ children, ...props }: any) => <pre {...props}>{children}</pre>,

    // Blockquotes
    blockquote: ({ children, ...props }: any) => <blockquote {...props}>{children}</blockquote>,

    // Tables
    table: ({ children, ...props }: any) => (
      <div className="table-wrapper">
        <table {...props}>{children}</table>
      </div>
    ),
    thead: ({ children, ...props }: any) => <thead {...props}>{children}</thead>,
    tbody: ({ children, ...props }: any) => <tbody {...props}>{children}</tbody>,
    tr: ({ children, ...props }: any) => <tr {...props}>{children}</tr>,
    th: ({ children, ...props }: any) => <th {...props}>{children}</th>,
    td: ({ children, ...props }: any) => <td {...props}>{children}</td>,

    // Horizontal rule
    hr: (props: any) => <hr {...props} />,

    // Strong and emphasis
    strong: ({ children, ...props }: any) => <strong {...props}>{children}</strong>,
    em: ({ children, ...props }: any) => <em {...props}>{children}</em>,
  }), [activeHeadingId]);

  return (
    <div className="manual-content">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeHighlight]}
        components={components}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
};
